import React, { useEffect, useState } from 'react';
import { FaSearch, FaUsers, FaUserTie, FaEnvelope } from 'react-icons/fa';
import { listCognitoUsers, listCognitoGroups } from '../api/users';
import {
  enableUser,
  disableUser,
  resetUserPassword,
  deleteUser,
  createUser,
  updateUser,
  setUserRole
} from '../api/adminUsers';
import { listAgentsWithEmails, updateAgentEmail } from '../api/agentEmails';
import ConfirmationModal from '../components/ConfirmationModal';
import ActionDropdown from '../components/ActionDropdown';
import Modal from '../components/Modal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import UserFormModal from '../components/UserFormModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import RoleModal from '../components/RoleModal';
import AgentEmailModal from '../components/AgentEmailModal';
import useDebounce from '../hooks/useDebounce';

const UserManagement = ({ setAlert }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('users');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [paginationToken, setPaginationToken] = useState();
  const [showCreate, setShowCreate] = useState(false);
  const [sortField, setSortField] = useState('Username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '' });
  const [editUserModal, setEditUserModal] = useState(null);
  const [resetUserModal, setResetUserModal] = useState(null);
  const [roleModal, setRoleModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const debouncedAgentSearch = useDebounce(agentSearch, 300);
  const [agentSortField, setAgentSortField] = useState('name');
  const [agentSortDirection, setAgentSortDirection] = useState('asc');
  const [agentPage, setAgentPage] = useState(1);
  const [filterEmail, setFilterEmail] = useState('all');
  const [editAgent, setEditAgent] = useState(null);
  const [preFillData, setPreFillData] = useState(null);

  useEffect(async () => {
    loadUsers();
    let grps = await listCognitoGroups()
    // console.log(`Groups: init ${JSON.stringify(grps)}`);
    setGroups(grps.groups || []); 
    // listCognitoGroups()
    //   .then((g) => setGroups(g.Groups || []))
    //   .catch((e) => console.error('Failed to load groups', e));
  }, []);
  // console.log(`Groups: ${groups}`);

  useEffect(() => {
    if (activeTab === 'agents' && agents.length === 0) {
      loadAgents();
    }
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    loadUsers();
  }, [debouncedSearch, selectedGroup]);


  const normalizeUser = (u) => {
    if (u.Username && u.Attributes) {
      return {
        ...u,
        UserStatus: u.UserStatus || u.userStatus || u.status
      };
    }

    const normalGroups = (() => {
      if (!u.groups) return undefined;
      if (Array.isArray(u.groups)) {
        return u.groups
          .map(g => {
            if (typeof g === 'string') return g;
            if (typeof g === 'object' && g) {
              return g.groupName || g.name || g.GroupName || '';
            }
            return '';
          })
          .filter(Boolean)
          .join(',');
      }
      if (typeof u.groups === 'object') {
        return u.groups.groupName || u.groups.name || u.groups.GroupName || '';
      }
      return u.groups;
    })();

    return {
      Username: u.username || u.Username,
      Enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
      UserStatus: u.UserStatus || u.userStatus || u.status,
      Attributes: [
        u.email ? { Name: 'email', Value: u.email } : null,
        normalGroups ? { Name: 'custom:groups', Value: normalGroups } : null,
      ].filter(Boolean),
    };
  };

  const loadUsers = async (tokenArg) => {
    setUsersLoading(true);
    try {
      const data = await listCognitoUsers(
        selectedGroup || null,
        debouncedSearch || null,
        tokenArg
      );
      let loaded = data.Users || data.users || data;
      if (Array.isArray(loaded)) {
        loaded = loaded.map(normalizeUser);
      } else if (loaded && Array.isArray(loaded.Users)) {
        loaded = loaded.Users.map(normalizeUser);
      } else {
        loaded = [];
      }
      if (tokenArg) {
        setUsers((prev) => [...prev, ...loaded]);
      } else {
        setUsers(loaded);
      }
      setPaginationToken(
        data.paginationToken || data.NextToken || data.nextToken || null
      );
    } catch (e) {
      console.error('Failed to load users', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load users" });
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAgents = async () => {
    setAgentsLoading(true);
    try {
      const data = await listAgentsWithEmails();
      setAgents(data);
    } catch (e) {
      console.error('Failed to load agents', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load agents" });
      }
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleAgentSort = (field) => {
    if (agentSortField === field) {
      setAgentSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setAgentSortField(field);
      setAgentSortDirection('asc');
    }
  };

  const handleAgentPageChange = (dir) => {
    setAgentPage((p) => {
      const total = Math.ceil(sortedAgents.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };

  const handleUpdateAgentEmail = async (agentName, email, role, gmUsername) => {
    try {
      await updateAgentEmail(agentName, email, role, gmUsername);
      setEditAgent(null);
      setConfirmation({
        open: true,
        title: 'Email Updated',
        message: email
          ? `Email for ${agentName} has been set to ${email}`
          : `Email for ${agentName} has been removed`
      });
      loadAgents();
    } catch (err) {
      console.error('Failed to update agent email', err);
      setConfirmation({
        open: true,
        title: 'Error',
        message: err.message || "Couldn't update agent email"
      });
    }
  };

  console.log(`Agents: ${JSON.stringify(agents)}`);

  // Filter and sort agents
  const filteredAgents = agents.filter((a) => {
    const matchesSearch =
      a.name.toLowerCase().includes(debouncedAgentSearch.toLowerCase()) ||
      (a.email && a.email.toLowerCase().includes(debouncedAgentSearch.toLowerCase()));
    const matchesEmailFilter =
      filterEmail === 'all' ||
      (filterEmail === 'with' && a.email) ||
      (filterEmail === 'without' && !a.email);
    return matchesSearch && matchesEmailFilter;
  });

  const sortedAgents = [...filteredAgents].sort((a, b) => {
    let aVal, bVal;
    switch (agentSortField) {
      case 'email':
        aVal = a.email || '';
        bVal = b.email || '';
        break;
      case 'updatedAt':
        aVal = a.updatedAt || '';
        bVal = b.updatedAt || '';
        break;
      default:
        aVal = a.name;
        bVal = b.name;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return agentSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return agentSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const agentTotalPages = Math.ceil(sortedAgents.length / PAGE_SIZE) || 1;
  const paginatedAgents = sortedAgents.slice((agentPage - 1) * PAGE_SIZE, agentPage * PAGE_SIZE);
  const agentsWithEmail = agents.filter((a) => a.email).length;
  const existingUserEmails = new Set(
    users.map(u => 
      u.Attributes?.find(a => a.Name === 'email')?.Value?.toLowerCase()
    ).filter(Boolean)
  );

  const handleEnable = (u) => {
    setConfirmAction({
      title: 'Enable User',
      message: `Enable ${u.Username}?`,
      onConfirm: async () => {
        await enableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };

  const handleDisable = (u) => {
    setConfirmAction({
      title: 'Disable User',
      message: `Disable ${u.Username}?`,
      onConfirm: async () => {
        await disableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };


  const handleReset = (u) => {
    setResetUserModal(u);
  };

  const handleDelete = (u) => {
    setDeleteUserName(u.Username);
  };

  const confirmDeleteUser = async () => {
    await deleteUser(deleteUserName);
    setDeleteUserName('');
    loadUsers();
  };

  const handleSetRole = (u) => {
    setRoleModal(u);
  };

  const handleEdit = (u) => {
    setEditUserModal(u);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (dir) => {
    setPage((p) => {
      const total = Math.ceil(sorted.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };


  const filtered = users.filter(u =>
    u.Username.toLowerCase().includes(search.toLowerCase()) ||
    (u.Attributes?.find(a => a.Name === 'email')?.Value || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal;
    let bVal;
    switch (sortField) {
      case 'Email':
        aVal = a.Attributes?.find(a => a.Name === 'email')?.Value || '';
        bVal = b.Attributes?.find(a => a.Name === 'email')?.Value || '';
        break;
      case 'Status':
        aVal = a.UserStatus;
        bVal = b.UserStatus;
        break;
      case 'Username':
      default:
        aVal = a.Username;
        bVal = b.Username;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
// Filter agents who have the role 'General Manager' or 'GM'
  const generalManagers = agents.filter(agent => 
    agent.role === 'General Manager' || agent.role === 'GM'
  );

  const JMListDropdown = ({ jms }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <div className="relative inline-block text-left mt-1">
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click events
            setIsOpen(!isOpen);
          }}
          className="flex items-center text-[10px] font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded border border-indigo-200 transition-colors"
        >
          Managing: {jms.length} JM(s)
          <svg className={`ml-1 w-3 h-3 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            {/* Transparent overlay to close when clicking outside */}
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
            
            <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-xl z-20 py-1">
              <div className="px-3 py-1 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Attached JMs
              </div>
              <div className="max-h-32 overflow-y-auto">
                {jms.map((jm, idx) => (
                  <div key={idx} className="px-3 py-2 hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0">
                    <span className="text-xs font-semibold text-gray-800">{jm.username}</span>
                    <span className="text-[10px] text-gray-500">{jm.email}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold">User Management</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'users'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FaUsers />
            Users
          </button>
          <button
            onClick={() => setActiveTab('agents')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'agents'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FaUserTie />
            Agent Emails
            {agentsWithEmail > 0 && (
              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                {agentsWithEmail}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              className="input-field pl-10 w-full"
              placeholder="Search users"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input-field sm:w-48"
            value={selectedGroup}
            onChange={e => {
              setSelectedGroup(e.target.value);
              setPaginationToken(null);
              loadUsers();
            }}
          >
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g.GroupName} value={g.GroupName}>{g.GroupName}</option>
            ))}
          </select>
          <button onClick={() => setShowCreate(true)} className="btn-primary whitespace-nowrap">Create User</button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th onClick={() => handleSort('Username')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Username {sortField === 'Username' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th onClick={() => handleSort('Email')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Email {sortField === 'Email' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th onClick={() => handleSort('Status')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">Status {sortField === 'Status' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
            <th className="px-6 py-3 text-left font-semibold text-gray-700">Groups</th>
            <th className="px-6 py-3" />
          </tr>
        </thead>
        {usersLoading ? (
          <tbody>
            <tr>
              <td colSpan="5" className="px-6 py-4 text-center">Loading users...</td>
            </tr>
          </tbody>
        ) : (
          <tbody className="bg-white divide-y divide-gray-200">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No users found</td>
              </tr>
            ) : paginated.map(u => {
              const email = u.Attributes?.find(a => a.Name === 'email')?.Value || '';
              const groups = u.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '';
              const isDisabled = u.Enabled === false;
              const status = u.UserStatus === 'UNCONFIRMED' ? 'Unconfirmed' : (isDisabled ? 'Disabled' : 'Enabled');
              return (
                <tr key={u.Username} className="hover:bg-gray-50 odd:bg-white even:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{u.Username}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${status === 'Enabled' ? 'status-active' : status === 'Disabled' ? 'status-inactive' : 'status-pending'}`}>{status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{groups}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <ActionDropdown
                      actions={[
                        isDisabled
                          ? { label: 'Enable', onClick: () => handleEnable(u) }
                          : { label: 'Disable', onClick: () => handleDisable(u) },
                        { label: 'Set Role', onClick: () => handleSetRole(u) },
                        { label: 'Edit', onClick: () => handleEdit(u) },
                        { label: 'Reset PW', onClick: () => handleReset(u) },
                        { label: 'Delete', onClick: () => handleDelete(u), className: 'text-red-600' },
                      ]}
                    />
                  </td>
                </tr>
            );
            })}
          </tbody>
        )}
      </table>
      </div>
      {paginationToken && (
        <div className="mt-4 text-center">
          <button onClick={() => loadUsers(paginationToken)} className="btn-outline">Load More</button>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex justify-end items-center space-x-3 mt-4">
          <button onClick={() => handlePageChange(-1)} disabled={page === 1} className="btn-outline text-sm">Prev</button>
          <span className="text-sm">Page {page} of {totalPages}</span>
          <button onClick={() => handlePageChange(1)} disabled={page >= totalPages} className="btn-outline text-sm">Next</button>
        </div>
      )}
        </>
      )}

              {/* Agents Tab Content */}
      {activeTab === 'agents' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                className="input-field pl-10 w-full"
                placeholder="Search agents by name or email"
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
              />
            </div>
            <select
              className="input-field sm:w-48"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
            >
              <option value="all">All Agents</option>
              <option value="with">With Email</option>
              <option value="without">Without Email</option>
            </select>
          </div>

          {/* Updated Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="text-sm text-gray-500">Total Agents</div>
              <div className="text-2xl font-bold">{agents.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <div className="text-sm text-gray-500">General Managers</div>
              <div className="text-2xl font-bold text-purple-600">
                {agents.filter(a => a.role === 'GM').length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
              <div className="text-sm text-gray-500">Junior Managers</div>
              <div className="text-2xl font-bold text-indigo-600">
                {agents.filter(a => a.role === 'JM').length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="text-sm text-gray-500">With Email</div>
              <div className="text-2xl font-bold text-green-600">{agentsWithEmail}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th onClick={() => handleAgentSort('name')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">
                    Agent Name {agentSortField === 'name' ? (agentSortDirection === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  {/* Added Role Column */}
                  <th className="px-6 py-3 text-left font-semibold text-gray-700">Role & Hierarchy</th>
                  <th onClick={() => handleAgentSort('email')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">
                    Email Address {agentSortField === 'email' ? (agentSortDirection === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleAgentSort('updatedAt')} className="px-6 py-3 text-left font-semibold text-gray-700 cursor-pointer">
                    Last Updated {agentSortField === 'updatedAt' ? (agentSortDirection === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              {agentsLoading ? (
                <tbody>
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">Loading agents...</td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedAgents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No agents found</td>
                    </tr>
                  ) : paginatedAgents.map((agent) => {
                    const isRegistered = agent.email && existingUserEmails.has(agent.email.toLowerCase());

                    return (
                      <tr key={agent.name} className="hover:bg-gray-50 odd:bg-white even:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{agent.name}</td>
                        
                        {/* Role & Hierarchy Cell */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-block w-fit ${
                              agent.role == 'GM' ? 'bg-purple-100 text-purple-800' :
                              agent.role == 'JM' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {agent.role || 'No Role'}
                            </span>
                            
                            {/* Show Hierarchy Links */}
                            {agent.role == 'JM' && agent.attachedGM && (
                              <span className="text-[10px] text-gray-500 mt-1">
                                Reports to: <b>{agent.attachedGM}</b>
                              </span>
                            )}
                            {(agent.role === 'General Manager' || agent.role === 'GM') && agent['attached-jm']?.length > 0 && (
                              <JMListDropdown jms={agent['attached-jm']} />
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {agent.email || <span className="text-gray-400 italic">Not set</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                          {agent.updatedAt ? new Date(agent.updatedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isRegistered ? (
                            <span className="status-badge status-active bg-blue-100 text-blue-800">
                              Cognito User Active
                            </span>
                          ) : agent.email ? (
                            <span className="status-badge status-active">
                              <FaEnvelope className="inline mr-1" />
                              Will receive reports
                            </span>
                          ) : (
                            <span className="status-badge status-inactive">No reports</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                          <button 
                            onClick={() => setEditAgent(agent)} 
                            disabled={isRegistered}
                            className={`btn-outline text-sm ${isRegistered ? 'opacity-50 cursor-not-allowed border-gray-300 text-gray-400' : ''}`}
                          >
                            {agent.email || agent.role ? 'Edit Details' : 'Set Details'}
                          </button>

                          {agent.email && (
                            <button 
                              onClick={() => setPreFillData({ 
                                username: agent.name, 
                                email: agent.email 
                              })} 
                              disabled={isRegistered}
                              className={`btn-primary text-sm ${isRegistered ? 'bg-gray-400 cursor-not-allowed border-none' : ''}`}
                            >
                              {isRegistered ? 'User Exists' : 'Create User'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          <AgentEmailModal
            open={!!editAgent}
            agent={editAgent}
            gmUsername={generalManagers}
            onClose={() => setEditAgent(null)}
            onSubmit={handleUpdateAgentEmail}
          />
        </>
      )}

      <UserFormModal
        open={showCreate || !!preFillData}
        title="Create User"
        groups={groups}
        initial={preFillData || {}}
        onClose={() => {
            setShowCreate(false);
            setPreFillData(null);
          }}
        onSubmit={async (data) => {
          try {
            await createUser({
              username: data.username,
              email: data.email,
              temporaryPassword: data.password || undefined,
              groups: data.groups
            });
            setConfirmation({ 
                    open: true, 
                    title: 'User Created', 
                    message: `User ${data.username} was created successfully.` 
                  });
            setShowCreate(false);
            setPreFillData(null);
            loadUsers();
          } catch (err) {
            console.error('Failed to create user', err);
            setConfirmation({ open: true, title: 'Error', message: "Couldn't create user" });
          }
        }}
      />

      {editUserModal && (
        <UserFormModal
          open={!!editUserModal}
          title={`Edit ${editUserModal.Username}`}
          groups={groups}
          initial={{
            username: editUserModal.Username,
            email: editUserModal.Attributes?.find(a => a.Name === 'email')?.Value || '',
            groups: (editUserModal.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '').split(',').filter(Boolean)
          }}
          onClose={() => setEditUserModal(null)}
          onSubmit={async (data) => {
            try {
              await updateUser(editUserModal.Username, { email: data.email });
              setEditUserModal(null);
              setConfirmation({ open: true, title: 'User Updated', message: 'User updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to update user', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't update user" });
            }
          }}
        />
      )}

      {resetUserModal && (
        <ResetPasswordModal
          open={!!resetUserModal}
          username={resetUserModal.Username}
          onClose={() => setResetUserModal(null)}
          onSubmit={async (pw) => {
            try {
              await resetUserPassword(resetUserModal.Username, pw);
              setResetUserModal(null);
              setConfirmation({ open: true, title: 'Password Reset', message: 'Temporary password set.' });
            } catch (err) {
              console.error('Failed to reset password', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't reset password" });
            }
          }}
        />
      )}

      {roleModal && (
        <RoleModal
          open={!!roleModal}
          username={roleModal.Username}
          onClose={() => setRoleModal(null)}
          onSubmit={async (role) => {
            try {
              await setUserRole(roleModal.Username, role);
              setRoleModal(null);
              setConfirmation({ open: true, title: 'Role Updated', message: 'Role updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to set role', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't set role" });
            }
          }}
        />
      )}

      {confirmAction && (
        <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)} title={confirmAction.title}>
          <p className="mb-4">{confirmAction.message}</p>
          <div className="flex justify-end space-x-2">
            <button onClick={() => setConfirmAction(null)} className="btn-outline">Cancel</button>
            <button
              onClick={async () => {
                await confirmAction.onConfirm();
              }}
              className="btn-primary"
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ open: false, title: '', message: '' })}
      />
      <DeleteConfirmationModal
        open={!!deleteUserName}
        itemName={deleteUserName || ''}
        onClose={() => setDeleteUserName('')}
        onConfirm={confirmDeleteUser}
      />
    </div>
  );
};

export default UserManagement;
